import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Typography, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useResolutionScale } from '../../hooks';
import { useMessageContextManager } from '../../components/MessageOverlay/MessageOverlay';
import { StyledTable } from '../../components';
import TableActionBar, { GradientButton } from '../../components/TableActionBar/TableActionBar';
import { OPERATIONS } from '../../constants';
import { updateOperations } from '../../redux/actionCreators/operations';
import { axiosInstance } from '../../api/api';
import styles from './WcpaAudit.module.scss';


// ─── Shared design tokens ──────────────────────────────────────────────────────
const card = {
	border: '1px solid',
	borderColor: 'divider',
	borderRadius: '8px',
	backgroundColor: 'background.paper',
	boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
};

const monoLabel = {
	fontSize: 11,
	fontFamily: "'IBM Plex Mono', monospace",
	fontWeight: 600,
	letterSpacing: '0.08em',
	textTransform: 'uppercase',
	color: 'text.secondary',
};

// ─── Badge ─────────────────────────────────────────────────────────────────────
const Badge = ({ color = 'amber', children, dot = true }) => {
	const colors = {
		amber: { bg: 'rgba(245,158,11,0.12)', text: '#f59e0b', border: 'rgba(245,158,11,0.3)' },
		green: { bg: 'rgba(34,197,94,0.12)', text: '#22c55e', border: 'rgba(34,197,94,0.3)' },
		red: { bg: 'rgba(239,68,68,0.1)', text: '#ef4444', border: 'rgba(239,68,68,0.2)' },
		blue: { bg: 'rgba(33,150,243,0.1)', text: '#2196F3', border: 'rgba(33,150,243,0.2)' },
		gray: { bg: 'rgba(128,128,128,0.1)', text: 'text.secondary', border: 'divider' },
	};
	const c = colors[color] || colors.amber;
	return (
		<Box sx={{
			display: 'inline-flex', alignItems: 'center', gap: '5px',
			px: '9px', py: '3px', borderRadius: '99px',
			fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", fontWeight: 500,
			backgroundColor: c.bg,
			color: c.text === 'text.secondary' ? 'text.secondary' : c.text,
			border: c.border === 'divider' ? '1px solid' : `1px solid ${c.border}`,
			borderColor: c.border === 'divider' ? 'divider' : undefined,
			whiteSpace: 'nowrap', flexShrink: 0,
		}}>
			{dot && <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: 'currentColor', flexShrink: 0 }} />}
			{children}
		</Box>
	);
};

const getLotTableColumns = (scaleValue) => [
	{ field: 'lotNumber', title: 'LOT NUMBER', width: 170, sortable: true },
	{ field: 'model', title: 'MODEL', width: 260, sortable: true },
	{ field: 'vehicleCount', title: 'TOTAL VINs', width: 120, align: 'center', sortable: true },
	{
		field: 'wtclReady',
		title: 'WTCL READY',
		width: 150,
		align: 'center',
		render: (row) => {
			const isComplete = row.wtclComplete === row.vehicleCount && row.vehicleCount > 0;
			const color = isComplete ? 'green' : row.wtclComplete > 0 ? 'amber' : 'red';
			return (
				<Badge color={color} dot>
					{row.wtclComplete} / {row.vehicleCount}
				</Badge>
			);
		},
	},
	{
		field: 'status',
		title: 'STATUS',
		width: 130,
		align: 'center',
		render: () => <Badge color="amber" dot>PENDING</Badge>,
	},
	{
		field: 'audited',
		title: 'AUDITED',
		width: 120,
		align: 'center',
		render: () => <Typography sx={{ color: 'text.disabled', fontSize: scaleValue(12), fontFamily: "'IBM Plex Mono', monospace" }}>—</Typography>,
	},
];

// ─── SCREEN 1: Lot Selection ───────────────────────────────────────────────────
const LotSelectionScreen = ({ eligibleData, loading, selectedLotIds, onSelectionChange, onStartAudit, scaleValue }) => {
	const lots = eligibleData;
	const totalVins = lots.reduce((s, entry) => s + (entry.vehicles?.length || 0), 0);
	const selectedVins = [...selectedLotIds].reduce((s, idx) => s + (eligibleData[idx]?.vehicles?.length || 0), 0);
	const [tableColumns, setTableColumns] = useState(() => getLotTableColumns(scaleValue));
	const [tableFilters, setTableFilters] = useState({});

	useEffect(() => {
		setTableColumns(getLotTableColumns(scaleValue));
	}, [scaleValue]);

	const tableRows = useMemo(() => lots.map((entry, idx) => {
		const vehicles = entry.vehicles || [];
		const wtclComplete = vehicles.filter((v) => v.wtclCompleted).length;
		return {
			id: idx,
			idx,
			lotNumber: entry.lotNumber,
			model: entry.model,
			vehicleCount: vehicles.length,
			wtclComplete,
			status: 'PENDING',
			audited: '—',
		};
	}), [lots]);

	return (
		<Box className={styles.wcpaPage}>

			{/* Stats bar */}
			<Box sx={{
				display: 'grid',
				gridTemplateColumns: 'repeat(4, 1fr)',
				...card,
				mb: 2.5,
				overflow: 'hidden',
				flexShrink: 0,
			}}>
				{[
					{ label: 'Total Lots', value: loading ? '—' : lots.length, accent: false },
					{ label: 'Total VINs', value: loading ? '—' : totalVins, accent: false },
					{ label: 'Selected Lots', value: selectedLotIds.size, accent: true },
					{ label: 'VINs in Selection', value: selectedVins, accent: true },
				].map(({ label, value, accent }, i, arr) => (
					<Box key={label} sx={{
						p: '14px 20px',
						borderRight: i < arr.length - 1 ? '1px solid' : 'none',
						borderColor: 'divider',
					}}>
						<Typography sx={{ ...monoLabel, mb: 0.5 }}>{label}</Typography>
						<Typography sx={{
							fontSize: 24, fontWeight: 700,
							fontFamily: "'IBM Plex Mono', monospace",
							color: accent ? '#f59e0b' : 'text.primary',
						}}>
							{value}
						</Typography>
					</Box>
				))}
			</Box>

			{/* Table area */}
			<Box sx={{
				mb: 2.5,
				flex: '0 1 auto',
				minHeight: 0,
				maxHeight: '100%',
				display: 'flex',
				flexDirection: 'column',
				overflow: 'hidden',
			}}>
				<TableActionBar
					showColumns={true}
					showFilter={true}
					rows={tableRows}
					columns={tableColumns}
					defaultColumns={getLotTableColumns(scaleValue)}
					onColumnChange={setTableColumns}
					onFilterChange={setTableFilters}
				/>
				<Box sx={{
					...card,
					flex: 1,
					minHeight: 0,
					overflow: 'auto',
				}}>
					{loading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
							<CircularProgress sx={{ color: '#f59e0b' }} />
						</Box>
					) : lots.length === 0 ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
							<Typography sx={{ color: 'text.secondary', fontFamily: "'IBM Plex Mono', monospace", fontSize: 13 }}>
								No eligible lots found
							</Typography>
						</Box>
					) : (
						<StyledTable
							rows={tableRows}
							columns={tableColumns}
							filters={tableFilters}
							elevated={false}
							showCheckBox={true}
							selectedRows={[...selectedLotIds]}
							onSelectionChange={(selectedIds) => onSelectionChange(new Set(selectedIds))}
							sx={{ height: '100%' }}
							rowStyle={(row) => ({
								backgroundColor: selectedLotIds.has(row.idx) ? 'rgba(245,158,11,0.06)' : undefined,
								'&:hover': {
									backgroundColor: selectedLotIds.has(row.idx) ? 'rgba(245,158,11,0.09)' : undefined,
								},
							})}
						/>
					)}
				</Box>
			</Box>

			{/* Footer */}
			<Box className={styles.footer} sx={{ flexShrink: 0 }}>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
					<Typography sx={{
						fontSize: scaleValue(13), color: 'text.secondary',
						fontFamily: "'IBM Plex Mono', monospace",
					}}>
						{selectedLotIds.size === 0 ? 'Select lots to begin audit' : (
							<>
								<Box component="span" sx={{ color: '#f59e0b', fontWeight: 700 }}>{selectedLotIds.size}</Box>
								{` lot${selectedLotIds.size > 1 ? 's' : ''} · `}
								<Box component="span" sx={{ color: '#f59e0b', fontWeight: 700 }}>{selectedVins}</Box>
								{' VINs selected'}
							</>
						)}
					</Typography>
					<GradientButton
						onClick={onStartAudit}
						gradient="linear-gradient(90deg, #f59e0b 0%, #f97316 100%)"
						sx={{ height: 48, px: 6, borderRadius: '10px', fontSize: 14 }}
						disabled={selectedLotIds.size === 0}
					>
						▶ Start Audit
					</GradientButton>
				</Box>
			</Box>
		</Box>
	);
};

// ─── VIN Card ──────────────────────────────────────────────────────────────────
const VinCard = ({ vin, lotId, action, onAction }) => {
	const isPassed = action === 'pass';
	const isFailed = action === 'fail';
	const hasAction = isPassed || isFailed;

	return (
		<Box sx={{
			...card,
			overflow: 'hidden',
			opacity: vin.wtclCompleted ? 1 : 0.5,
			border: '1px solid',
			borderColor: isPassed
				? 'rgba(34,197,94,0.5)'
				: isFailed
					? 'rgba(239,68,68,0.4)'
					: 'divider',
			backgroundColor: isPassed
				? 'rgba(34,197,94,0.04)'
				: isFailed
					? 'rgba(239,68,68,0.04)'
					: 'background.paper',
			transition: 'all 0.2s',
		}}>
			{/* Card header */}
			<Box sx={{
				display: 'flex', alignItems: 'center', justifyContent: 'space-between',
				px: 1.75, py: 1.25,
				borderBottom: '1px solid', borderColor: 'divider',
				backgroundColor: 'background.default',
			}}>
				<Typography sx={{
					fontFamily: "'IBM Plex Mono', monospace",
					fontSize: 11, fontWeight: 700, color: 'text.primary',
					letterSpacing: '0.04em', overflow: 'hidden',
					textOverflow: 'ellipsis', whiteSpace: 'nowrap', mr: 1,
				}}>
					{vin.serialNumber}
				</Typography>
				{isPassed
					? <Badge color="green" dot={false}>✓ PASS</Badge>
					: isFailed
						? <Badge color="red" dot={false}>✗ FAIL</Badge>
						: vin.wtclCompleted
							? <Badge color="amber" dot>READY</Badge>
							: <Badge color="gray" dot>LOCKED</Badge>
				}
			</Box>

			{/* Card body */}
			<Box sx={{ px: 1.75, py: 1.25 }}>
				{[
					{ label: 'PART NO.',     value: vin.partNumber || '—'},
					{ label: 'MODEL / YR',   value: `${vin.model || '—'} ${vin.modelYear || ''}`.trim() },
					{ label: 'DESCRIPTION',  value: vin.partDescription || '—'},
					{ label: 'ENGINE S.NO.', value: vin.engineSerialNumber || '—'},
				].map(({ label, value }) => (
					<Box key={label} sx={{
						display: 'flex', justifyContent: 'space-between',
						alignItems: 'baseline', py: 0.35, gap: 1,
					}}>
						<Typography sx={{ fontSize: 10, fontFamily: "'IBM Plex Mono', monospace", color: 'text.secondary', whiteSpace: 'nowrap' }}>
							{label}
						</Typography>
						<Typography sx={{ fontSize: 11, color: 'text.primary', fontWeight: 500, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
							{value || '—'}
						</Typography>
					</Box>
				))}
			</Box>

			{/* Card footer — WTCL status + Pass/Fail */}
			<Box sx={{
				px: 1.75, py: 1.25,
				borderTop: '1px solid', borderColor: 'divider',
				backgroundColor: 'rgba(0,0,0,0.02)',
				display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
			}}>
				<Box sx={{
					px: 1, py: 0.4, borderRadius: '4px',
					fontSize: 10, fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600,
					backgroundColor: vin.wtclCompleted ? 'rgba(34,197,94,0.1)' : 'rgba(0,0,0,0.05)',
					color: vin.wtclCompleted ? '#22c55e' : 'text.disabled',
					border: '1px solid',
					borderColor: vin.wtclCompleted ? 'rgba(34,197,94,0.3)' : 'divider',
					whiteSpace: 'nowrap', flexShrink: 0,
				}}>
					WTCL {vin.wtclCompleted ? '✓' : '✗'}
				</Box>

				{vin.wtclCompleted && (
					<Box sx={{ display: 'flex', gap: 0.75 }}>
						{hasAction ? (
							/* Already actioned — allow undo via clicking the other */
							<>
								<Box
									onClick={() => onAction(vin, lotId, true)}
									sx={{
										px: 1.25, py: 0.4, borderRadius: '4px', cursor: 'pointer',
										fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600,
										backgroundColor: isPassed ? 'rgba(34,197,94,0.18)' : 'rgba(34,197,94,0.06)',
										color: isPassed ? '#22c55e' : 'text.disabled',
										border: `1px solid ${isPassed ? 'rgba(34,197,94,0.4)' : 'divider'}`,
										display: 'flex', alignItems: 'center', gap: 0.5,
										'&:hover': { backgroundColor: 'rgba(34,197,94,0.15)' },
										transition: 'all 0.15s',
									}}
								>
									<CheckCircleIcon sx={{ fontSize: 11 }} /> PASS
								</Box>
								<Box
									onClick={() => onAction(vin, lotId, false)}
									sx={{
										px: 1.25, py: 0.4, borderRadius: '4px', cursor: 'pointer',
										fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600,
										backgroundColor: isFailed ? 'rgba(239,68,68,0.15)' : 'rgba(239,68,68,0.06)',
										color: isFailed ? '#ef4444' : 'text.disabled',
										border: `1px solid ${isFailed ? 'rgba(239,68,68,0.35)' : 'divider'}`,
										display: 'flex', alignItems: 'center', gap: 0.5,
										'&:hover': { backgroundColor: 'rgba(239,68,68,0.12)' },
										transition: 'all 0.15s',
									}}
								>
									<CancelIcon sx={{ fontSize: 11 }} /> FAIL
								</Box>
							</>
						) : (
							/* No action yet */
							<>
								<Box
									onClick={() => onAction(vin, lotId, true)}
									sx={{
										px: 1.5, py: 0.5, borderRadius: '4px', cursor: 'pointer',
										fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700,
										backgroundColor: 'rgba(34,197,94,0.1)',
										color: '#22c55e',
										border: '1px solid rgba(34,197,94,0.3)',
										display: 'flex', alignItems: 'center', gap: 0.5,
										'&:hover': { backgroundColor: 'rgba(34,197,94,0.2)', transform: 'scale(1.02)' },
										transition: 'all 0.15s',
									}}
								>
									<CheckCircleIcon sx={{ fontSize: 12 }} /> PASS
								</Box>
								<Box
									onClick={() => onAction(vin, lotId, false)}
									sx={{
										px: 1.5, py: 0.5, borderRadius: '4px', cursor: 'pointer',
										fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700,
										backgroundColor: 'rgba(239,68,68,0.08)',
										color: '#ef4444',
										border: '1px solid rgba(239,68,68,0.25)',
										display: 'flex', alignItems: 'center', gap: 0.5,
										'&:hover': { backgroundColor: 'rgba(239,68,68,0.18)', transform: 'scale(1.02)' },
										transition: 'all 0.15s',
									}}
								>
									<CancelIcon sx={{ fontSize: 12 }} /> FAIL
								</Box>
							</>
						)}
					</Box>
				)}
			</Box>
		</Box>
	);
};

// ─── SCREEN 2: VIN Audit (all lots in one view) ───────────────────────────────
const VinAuditScreen = ({
	eligibleData, selectedLotIds, vinActions,
	onVinAction, onGoBack, onSubmit, submitting, scaleValue,
}) => {
	const lotEntries = [...selectedLotIds].map(idx => ({ idx, ...eligibleData[idx] }));
	const totalVins = lotEntries.reduce((s, e) => s + (e.vehicles?.length || 0), 0);
	const passedCount = Object.values(vinActions).filter((v) => v === 'pass').length;
	const failedCount = Object.values(vinActions).filter((v) => v === 'fail').length;
	const atLeastOnePass = passedCount > 0;

	return (
		<Box className={styles.wcpaPage}>

			{/* Header */}
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5, gap: 2, flexWrap: 'wrap' }}>
				<Box>
					<Typography sx={{ ...monoLabel, mb: 0.5 }}>VIN AUDIT</Typography>
					<Typography sx={{ fontSize: 22, fontWeight: 700, color: 'text.primary', mb: 1 }}>
						{lotEntries.length} Lot{lotEntries.length > 1 ? 's' : ''} · {totalVins} VIN{totalVins !== 1 ? 's' : ''}
					</Typography>
					<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
						{lotEntries.map(e => <Badge key={e.idx} color="blue" dot>{e.lotNumber}</Badge>)}
					</Box>
				</Box>
				<Box
					onClick={onGoBack}
					sx={{
						display: 'inline-flex', alignItems: 'center', gap: 0.75,
						px: 2, py: 1,
						...card,
						color: 'text.secondary',
						cursor: 'pointer', fontSize: 13,
						fontFamily: "'IBM Plex Mono', monospace",
						'&:hover': { backgroundColor: 'action.hover', color: 'text.primary' },
						transition: 'all 0.15s',
					}}
				>
					<ArrowBackIcon fontSize="small" /> Back to Lots
				</Box>
			</Box>

			{/* Progress summary bar */}
			<Box sx={{
				display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
				...card, mb: 2.5, overflow: 'hidden',
			}}>
				{[
					{ label: 'Total VINs', value: totalVins, color: 'text.primary' },
					{ label: 'Passed', value: passedCount, color: '#22c55e' },
					{ label: 'Failed', value: failedCount, color: '#ef4444' },
				].map(({ label, value, color }, i, arr) => (
					<Box key={label} sx={{
						p: '12px 18px',
						borderRight: i < arr.length - 1 ? '1px solid' : 'none',
						borderColor: 'divider',
					}}>
						<Typography sx={{ ...monoLabel, mb: 0.5 }}>{label}</Typography>
						<Typography sx={{ fontSize: 22, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", color }}>
							{value}
						</Typography>
					</Box>
				))}
			</Box>

			{/* All lot sections — scrollable */}
			<Box sx={{ flex: 1, overflow: 'auto', pr: 0.5 }}>
				{lotEntries.map(({ idx, lotNumber, model, vehicles = [] }, i) => {
					const wtclDone = vehicles.filter((v) => v.wtclCompleted).length;
					const lotPassed = vehicles.filter((v) => vinActions[v.serialNumber] === 'pass').length;
					const lotFailed = vehicles.filter((v) => vinActions[v.serialNumber] === 'fail').length;

					return (
						<Box key={`${lotNumber}-${model}-${idx}`} sx={{ mb: i < lotEntries.length - 1 ? 3 : 0 }}>
							{/* Lot section header */}
							<Box sx={{
								display: 'flex', alignItems: 'center', justifyContent: 'space-between',
								flexWrap: 'wrap', gap: 1,
								px: 2, py: 1.25,
								...card,
								mb: 1.5,
								backgroundColor: 'background.default',
							}}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
									<Typography sx={{
										fontFamily: "'IBM Plex Mono', monospace",
										fontSize: 13, fontWeight: 700, color: '#f59e0b',
										letterSpacing: '0.06em',
									}}>
										{lotNumber}
									</Typography>
									<Typography sx={{ ...monoLabel, color: 'text.secondary', fontSize: 10 }}>
										{vehicles.length} VIN{vehicles.length !== 1 ? 's' : ''}
									</Typography>
									<Badge color={wtclDone === vehicles.length ? 'green' : 'amber'} dot>
										WTCL {wtclDone}/{vehicles.length}
									</Badge>
								</Box>
								<Box sx={{ display: 'flex', gap: 1 }}>
									{lotPassed > 0 && <Badge color="green" dot={false}>✓ {lotPassed} Passed</Badge>}
									{lotFailed > 0 && <Badge color="red" dot={false}>✗ {lotFailed} Failed</Badge>}
									{lotPassed === 0 && lotFailed === 0 && <Badge color="gray" dot>Pending</Badge>}
								</Box>
							</Box>

							{/* VIN cards grid */}
							<Box sx={{
								display: 'grid',
								gridTemplateColumns: {
									xs: '1fr',
									sm: 'repeat(auto-fill, minmax(min(340px, 100%), 1fr))',
									md: 'repeat(auto-fill, minmax(min(440px, 100%), 1fr))',
									lg: 'repeat(auto-fill, minmax(min(540px, 100%), 1fr))',
								},
								gap: '10px',
							}}>
								{vehicles.map((vin) => (
									<VinCard
										key={vin.serialNumber}
										vin={vin}
										lotId={lotNumber}
										action={vinActions[vin.serialNumber] || null}
										onAction={onVinAction}
									/>
								))}
							</Box>
						</Box>
					);
				})}
			</Box>

			{/* Footer */}
			<Box className={styles.footer} sx={{ mt: 2 }}>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>

					{/* Status summary */}
					<Typography sx={{
						fontSize: scaleValue(13),
						fontFamily: "'IBM Plex Mono', monospace",
						color: 'text.secondary',
					}}>
						{atLeastOnePass ? (
							<>
								<Box component="span" sx={{ color: '#22c55e', fontWeight: 700 }}>{passedCount}</Box>
								{' passed'}
								{failedCount > 0 && (
									<> · <Box component="span" sx={{ color: '#ef4444', fontWeight: 700 }}>{failedCount}</Box>{' failed'}</>
								)}
							</>
						) : (
							'Mark VINs as pass or fail to proceed'
						)}
					</Typography>

					<Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
						<Box
							onClick={onGoBack}
							sx={{
								display: 'inline-flex', alignItems: 'center', gap: 1,
								px: 3, height: 44,
								...card,
								color: 'text.secondary',
								cursor: 'pointer', fontSize: 13,
								fontWeight: 600,
								fontFamily: "'IBM Plex Mono', monospace",
								'&:hover': { backgroundColor: 'action.hover', color: 'text.primary' },
								transition: 'all 0.15s',
							}}
						>
							← Back
						</Box>

						<GradientButton
							onClick={onSubmit}
							gradient={atLeastOnePass
								? 'linear-gradient(90deg, #2196F3 0%, #21CCE1 100%)'
								: 'linear-gradient(90deg, #666 0%, #888 100%)'}
							sx={{ height: 44, px: 5, borderRadius: '10px', fontSize: 13 }}
							disabled={!atLeastOnePass || submitting}
						>
							{submitting
								? <CircularProgress size={18} sx={{ color: '#fff' }} />
								: '✓ Mark WCPA'
							}
						</GradientButton>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

// ─── Main ──────────────────────────────────────────────────────────────────────
const WcpaAudit = () => {
	const { scaleValue } = useResolutionScale();
	const { showMessageOverlay } = useMessageContextManager();
	const dispatch = useDispatch();

	const [screen, setScreen] = useState('lots');
	const [eligibleData, setEligibleData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [selectedLotIds, setSelectedLotIds] = useState(new Set());
	const [vinActions, setVinActions] = useState({});   // { [serialNumber]: 'pass' | 'fail' }
	const [submitting, setSubmitting] = useState(false);

	const fetchEligible = useCallback(async () => {
		setLoading(true);
		try {
			const { data } = await axiosInstance.get('/wcpa/eligible');
			setEligibleData(data || []);
		} catch {
			showMessageOverlay({ contextText: 'Failed to load eligible lots', error: true });
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchEligible();
		dispatch(updateOperations({ currentOperation: OPERATIONS.WCPA }));
	}, []);

	const handleLotSelectionChange = (nextSelection) => {
		setSelectedLotIds(nextSelection);
	};

	const handleStartAudit = () => {
		setVinActions({});
		setScreen('audit');
	};

	const handleVinAction = (vin, lotId, isPass) => {
		const sn = vin.serialNumber;
		if (vinActions[sn] === (isPass ? 'pass' : 'fail')) return;
		setVinActions((prev) => ({ ...prev, [sn]: isPass ? 'pass' : 'fail' }));
	};

	const handleSubmit = async () => {
		setSubmitting(true);
		const closeOverlay = showMessageOverlay({ contextText: 'Submitting WCPA Audit...', loading: true });
		try {
			const payload = [];
			for (const idx of selectedLotIds) {
				const { lotNumber, vehicles = [] } = eligibleData[idx];
				for (const vin of vehicles) {
					if (!vin?.serialNumber) continue;
					const action = vinActions[vin.serialNumber];
					if (action) {
						payload.push({
							lotNumber,
							model: vin.model,
							serialNumber: vin.serialNumber,
							cfa: vin.cfa ?? true,
							tfs: vin.tfs ?? false,
							markAudit: action === 'pass',
						});
					}
				}
			}
			console.log('[WCPA] submitting payload:', payload);
			await axiosInstance.post('/wcpa/complete-by-lot', payload);
			closeOverlay();
			const lotLabel = [...selectedLotIds]
				.map(idx => `${eligibleData[idx].lotNumber} (${eligibleData[idx].model})`)
				.join(', ');
			showMessageOverlay({
				contextText: `Lot ${lotLabel} — WCPA Audit Complete`,
				success: true,
			});
			setSelectedLotIds(new Set());
			setVinActions({});
			setScreen('lots');
			fetchEligible();
		} catch (err) {
			console.error('[WCPA] submit error:', err);
			closeOverlay();
			showMessageOverlay({ contextText: 'Submission Failed', info: err?.response?.data?.message || err?.message || 'An error occurred', error: true });
		} finally {
			setSubmitting(false);
		}
	};

	if (screen === 'lots') {
		return (
			<LotSelectionScreen
				eligibleData={eligibleData}
				loading={loading}
				selectedLotIds={selectedLotIds}
				onSelectionChange={handleLotSelectionChange}
				onStartAudit={handleStartAudit}
				scaleValue={scaleValue}
			/>
		);
	}

	return (
		<VinAuditScreen
			eligibleData={eligibleData}
			selectedLotIds={selectedLotIds}
			vinActions={vinActions}
			onVinAction={handleVinAction}
			onGoBack={() => setScreen('lots')}
			onSubmit={handleSubmit}
			submitting={submitting}
			scaleValue={scaleValue}
		/>
	);
};

export default WcpaAudit;